import { Button } from "@fawkes/ui";

/**
 * De voetregel onder een tabel: hoeveel je ziet van hoeveel, genummerde pagina's, en hoeveel rijen per
 * pagina.
 *
 * <p>De schermen hadden allemaal hun eigen "Vorige — Pagina 1 van 2 — Volgende". Dat werkt, maar je kunt
 * er niet mee naar pagina 4 zonder drie keer te klikken, en het aantal rijen lag vast op tien. Op acc
 * staan er genummerde knoppen en een keuzelijst, en dat is ook praktischer: met zestien tarieven wil je
 * ze gewoon allemaal zien.
 *
 * <p>Bij veel pagina's wordt het venster afgekapt met een beletselteken — de eerste, de laatste, en wat
 * er rond de huidige past. Twintig knoppen naast elkaar is geen navigatie meer.
 */
export function TablePager({
  page,
  pageCount,
  total,
  from,
  to,
  pageSize,
  pageSizes = [10, 25, 50, 100],
  onPage,
  onPageSize,
  labels,
}: {
  page: number;
  pageCount: number;
  total: number;
  /** Eerste en laatste rijnummer op deze pagina, 1-based. */
  from: number;
  to: number;
  pageSize: number;
  pageSizes?: number[];
  onPage: (page: number) => void;
  /** Ontbreekt hij, dan staat de keuzelijst er niet — sommige lijsten hebben een vaste maat. */
  onPageSize?: (size: number) => void;
  labels: {
    /** "%d–%d van %d resultaten" */
    range: string;
    previous: string;
    next: string;
    /** "Rijen per pagina:" */
    pageSize: string;
    /** "Pagina %d" — de naam van een genummerde knop, voor schermlezers. */
    page: string;
  };
}) {
  if (total <= 0) return null;

  return (
    <div className="fawkes-relatie-list__pager">
      <span className="fawkes-relatie-list__pager-range">
        {labels.range.replace("%d", String(from)).replace("%d", String(to)).replace("%d", String(total))}
      </span>

      <div className="fawkes-relatie-list__pager-controls">
        <Button
          variant="secondary"
          className="fawkes-pager__step"
          onClick={() => onPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          aria-label={labels.previous}
          title={labels.previous}
        >
          ‹
        </Button>
        {vensterVan(page, pageCount).map((nummer, i) =>
          nummer === null ? (
            <span key={`gat-${i}`} className="fawkes-pager__gap" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={nummer}
              type="button"
              className={`fawkes-pager__page${nummer === page ? " fawkes-pager__page--active" : ""}`}
              aria-current={nummer === page ? "page" : undefined}
              aria-label={labels.page.replace("%d", String(nummer))}
              onClick={() => onPage(nummer)}
            >
              {nummer}
            </button>
          ),
        )}
        <Button
          variant="secondary"
          className="fawkes-pager__step"
          onClick={() => onPage(Math.min(pageCount, page + 1))}
          disabled={page >= pageCount}
          aria-label={labels.next}
          title={labels.next}
        >
          ›
        </Button>
      </div>

      {onPageSize ? (
        <label className="fawkes-pager__size">
          <span>{labels.pageSize}</span>
          <select
            className="fui-field__input"
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
          >
            {pageSizes.map((maat) => (
              <option key={maat} value={maat}>
                {maat}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
}

/**
 * Welke paginanummers je te zien krijgt: altijd de eerste en de laatste, en het buurtje rond waar je
 * nu bent. `null` staat voor een gat.
 */
export function vensterVan(page: number, pageCount: number, breedte = 1): (number | null)[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const nummers = new Set<number>([1, pageCount, page]);
  for (let d = 1; d <= breedte; d += 1) {
    if (page - d >= 1) nummers.add(page - d);
    if (page + d <= pageCount) nummers.add(page + d);
  }
  const gesorteerd = [...nummers].sort((a, b) => a - b);
  const uit: (number | null)[] = [];
  let vorige = 0;
  for (const nummer of gesorteerd) {
    if (vorige && nummer - vorige > 1) uit.push(null);
    uit.push(nummer);
    vorige = nummer;
  }
  return uit;
}
